/**
 * NewTemplateModal — May 2026 redesign per templates_editor_v3.html.
 *
 * Three paths to a new template:
 *   1. AI prompt → calls POST /api/templates/generate then opens the
 *      result in the editor unsaved (user reviews + saves).
 *   2. Build manually → picks Email/SMS/Dialer, optional segment pills
 *      (for AI context), name + tags → opens blank editor.
 *   3. Create blank (footer) → fast path, opens blank Email template.
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Mail, MessageSquare, Phone, Users, X, Loader2, Upload, FileText, Lightbulb } from "lucide-react";
import { API, useAuth } from "@/App";

const TYPES = [
  { key: "email",  Icon: Mail,          label: "Email",         desc: "Subject + body" },
  { key: "sms",    Icon: MessageSquare, label: "SMS",           desc: "160 char segments" },
  { key: "dialer", Icon: Phone,         label: "Dialer script", desc: "Section-based editor" },
];

const SEGMENT_SUGGESTIONS = [
  { key: "ccm_cardiology",   label: "CCM — Cardiology",       count: 412 },
  { key: "rpm_copd",         label: "RPM — COPD",             count: 201 },
  { key: "legacy_ehr",       label: "Legacy EHR",             count: 267 },
  { key: "rcm_denial_prone", label: "RCM — denial-prone",     count: 389 },
  { key: "awv_wedge",        label: "AWV wedge",              count: 331 },
];

export default function NewTemplateModal({ onClose, onDraftReady }) {
  const { token } = useAuth();
  const [prompt, setPrompt] = useState("");
  const [pickedType, setPickedType] = useState("email");
  const [pickedSegments, setPickedSegments] = useState([]);
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [generating, setGenerating] = useState(false);

  // Upload-to-template state
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null); // { draft, suggestions, warnings, source }

  const toggleSegment = (key) =>
    setPickedSegments((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));

  // 1) AI generate path
  const handleGenerate = async () => {
    const purpose = prompt.trim();
    if (!purpose) { toast.error("Describe what you want first"); return; }
    setGenerating(true);
    try {
      const segCtx = pickedSegments
        .map((k) => SEGMENT_SUGGESTIONS.find((s) => s.key === k)?.label)
        .filter(Boolean)
        .join(", ");
      const r = await axios.post(`${API}/templates/generate`, {
        type: pickedType, purpose,
        context: segCtx ? `Target segments: ${segCtx}` : "",
        tone: "professional",
      }, { headers: { Authorization: `Bearer ${token}` } });
      const ai = r.data || {};
      const draft = {
        type: pickedType,
        name: name.trim() || `New ${TYPES.find((t) => t.key === pickedType)?.label}`,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
        status: "draft",
      };
      if (pickedType === "email") {
        draft.subject      = ai.subject || "";
        draft.preview_text = ai.preview_text || "";
        draft.body         = ai.body || "";
      } else if (pickedType === "sms") {
        draft.body = ai.body || "";
      } else {
        draft.sections = ai.sections || [];
      }
      toast.success("AI draft ready — review and save when you're happy");
      onDraftReady(draft);
    } catch (e) {
      toast.error("AI generate failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setGenerating(false);
    }
  };

  // 2/3) Blank path (manual create)
  const handleBlank = () => {
    const draft = {
      type: pickedType,
      name: name.trim() || `New ${TYPES.find((t) => t.key === pickedType)?.label}`,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: "draft",
    };
    if (pickedType === "email") { draft.subject = ""; draft.preview_text = ""; draft.body = ""; }
    if (pickedType === "sms")   { draft.body = ""; }
    if (pickedType === "dialer"){ draft.sections = [{ id: crypto.randomUUID(), title: "Opener", body: "" }]; }
    onDraftReady(draft);
  };

  // 4) Upload + AI convert path — the user uploads a script/document
  // (PDF/DOCX/TXT) and AI converts it into a {pickedType}-shaped draft.
  // The draft is previewed inside the modal; "Use draft" hands it off
  // to the editor (same code path as the AI generate flow).
  const onUploadPick = (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "docx", "txt"].includes(ext)) {
      toast.error("Unsupported file type — please use PDF, DOCX, or TXT");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5MB)");
      return;
    }
    setUploadFile(file);
    setUploadPreview(null);
  };

  const handleUploadConvert = async () => {
    if (!uploadFile) { toast.error("Pick a file first"); return; }
    setUploading(true);
    setUploadPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("target_type", pickedType);
      fd.append("name", name.trim());
      fd.append("tone", "professional");
      const r = await axios.post(`${API}/templates/ai/upload-convert`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploadPreview(r.data);
      toast.success("AI converted your script — review and use draft");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  const handleUseUploadDraft = () => {
    if (!uploadPreview) return;
    const ai = uploadPreview;
    const draft = {
      type: pickedType,
      name: name.trim() || ai.name || `New ${TYPES.find((t) => t.key === pickedType)?.label}`,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: "draft",
    };
    if (pickedType === "email") {
      draft.subject = ai.subject || "";
      draft.body = ai.body || "";
    } else if (pickedType === "sms") {
      draft.body = ai.body || "";
    } else {
      draft.sections = (ai.sections || []).map((s) => ({
        id: crypto.randomUUID(), title: s.title || "Section", body: s.body || "",
      }));
    }
    onDraftReady(draft);
  };

  const clearUpload = () => { setUploadFile(null); setUploadPreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; };

  return (
    <div
      data-testid="new-template-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 10002, padding: 24,
      }}
    >
      <div
        style={{
          width: "min(720px, 100%)", maxHeight: "92vh",
          background: "var(--surface,#fff)", borderRadius: 14,
          display: "flex", flexDirection: "column", overflow: "hidden",
          fontFamily: '"DM Sans", -apple-system, sans-serif',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "14px 18px", borderBottom: "0.5px solid var(--border)",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600 }}>New template</div>
          <button
            data-testid="new-tpl-close"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {/* Type picker — first decision, drives both AI generate + Upload */}
          <FieldLabel>What kind of template are you building?</FieldLabel>
          <div data-testid="new-tpl-type-picker" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 }}>
            {TYPES.map(({ key, Icon, label, desc }) => {
              const sel = pickedType === key;
              return (
                <button
                  key={key}
                  data-testid={`new-tpl-type-${key}`}
                  onClick={() => { setPickedType(key); setUploadPreview(null); }}
                  style={{
                    padding: "12px 10px", borderRadius: 10,
                    border: sel ? "1.5px solid #0071e3" : "0.5px solid var(--border)",
                    background: sel ? "rgba(0,113,227,0.05)" : "var(--surface,#fff)",
                    textAlign: "left", cursor: "pointer", fontFamily: "inherit",
                    display: "flex", flexDirection: "column", gap: 4,
                  }}
                >
                  <Icon style={{ width: 16, height: 16, color: sel ? "#0071e3" : "var(--text-tertiary)" }} />
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>{desc}</div>
                </button>
              );
            })}
          </div>

          {/* AI panel */}
          <div
            style={{
              background: "rgba(139,92,246,0.06)",
              border: "0.5px solid rgba(139,92,246,0.18)",
              borderRadius: 12, padding: 14, marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#5b21b6", marginBottom: 8 }}>
              <Sparkles style={{ width: 13, height: 13 }} />
              <span>✦ Describe what you want — AI builds it</span>
            </div>
            <textarea
              data-testid="new-tpl-ai-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder={
                'e.g. "An outbound email for cardiology practices with 200+ Medicare patients introducing CCM and RPM — conversational tone, no EHR switch required"\n\nOr: "A 2-sentence SMS reminding a lead about their demo tomorrow with confirm / reschedule options"'
              }
              style={{
                width: "100%", padding: "10px 12px",
                border: "0.5px solid rgba(139,92,246,0.25)",
                borderRadius: 10, fontSize: 12.5, fontFamily: "inherit",
                background: "var(--surface,#fff)", resize: "vertical", outline: "none",
                boxSizing: "border-box", color: "var(--text-primary)",
              }}
            />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8, gap: 8 }}>
              <div style={{ fontSize: 10.5, color: "var(--text-tertiary)" }}>
                AI generates subject, body, and merge fields automatically
              </div>
              <button
                data-testid="new-tpl-ai-generate"
                onClick={handleGenerate}
                disabled={generating}
                style={{
                  background: "#7c3aed", color: "#fff", border: "none",
                  padding: "7px 14px", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, cursor: generating ? "wait" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                  fontFamily: "inherit", opacity: generating ? 0.7 : 1,
                }}
              >
                {generating
                  ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} />
                  : <Sparkles style={{ width: 12, height: 12 }} />}
                {generating ? "Generating…" : "Generate template"}
              </button>
            </div>
          </div>

          {/* Upload-to-template panel */}
          <div
            data-testid="new-tpl-upload-panel"
            style={{
              background: "rgba(0,113,227,0.04)",
              border: "0.5px solid rgba(0,113,227,0.18)",
              borderRadius: 12, padding: 14, marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "#0071e3", marginBottom: 8 }}>
              <Upload style={{ width: 13, height: 13 }} />
              <span>Upload a script — AI converts it to a {TYPES.find((t) => t.key === pickedType)?.label || "template"}</span>
            </div>
            <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", marginBottom: 10, lineHeight: 1.5 }}>
              Drop a PDF, DOCX, or TXT (max 5MB) — AI extracts the content and reshapes it into the type you picked above. You'll get a preview + suggestions before saving.
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                data-testid="new-tpl-upload-file"
                onChange={(e) => onUploadPick(e.target.files?.[0])}
                style={{ display: "none" }}
              />
              <button
                data-testid="new-tpl-upload-pick"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: "7px 14px", borderRadius: 8,
                  border: "0.5px solid var(--border)", background: "var(--surface,#fff)",
                  fontSize: 12, fontWeight: 500, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
                }}
              >
                <Upload style={{ width: 12, height: 12 }} /> Choose file
              </button>
              {uploadFile && (
                <div data-testid="new-tpl-upload-filename" style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: 4, overflow: "hidden" }}>
                  <FileText style={{ width: 11, height: 11, flexShrink: 0 }} />
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {uploadFile.name} <span style={{ color: "var(--text-tertiary)" }}>({Math.ceil(uploadFile.size / 1024)} KB)</span>
                  </span>
                  <button
                    data-testid="new-tpl-upload-clear"
                    onClick={clearUpload}
                    aria-label="Remove file"
                    style={{ background: "none", border: "none", color: "var(--text-tertiary)", cursor: "pointer", padding: 2 }}
                  >
                    <X style={{ width: 11, height: 11 }} />
                  </button>
                </div>
              )}
              <button
                data-testid="new-tpl-upload-convert"
                onClick={handleUploadConvert}
                disabled={!uploadFile || uploading}
                style={{
                  marginLeft: "auto",
                  background: "#0071e3", color: "#fff", border: "none",
                  padding: "7px 14px", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, cursor: (!uploadFile || uploading) ? "not-allowed" : "pointer",
                  display: "inline-flex", alignItems: "center", gap: 5,
                  fontFamily: "inherit", opacity: (!uploadFile || uploading) ? 0.5 : 1,
                }}
              >
                {uploading
                  ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} />
                  : <Sparkles style={{ width: 12, height: 12 }} />}
                {uploading ? "Converting…" : `Convert with AI → ${TYPES.find((t) => t.key === pickedType)?.label}`}
              </button>
            </div>

            {/* Preview */}
            {uploadPreview && (
              <div
                data-testid="new-tpl-upload-preview"
                style={{
                  marginTop: 12, padding: 12, background: "var(--surface,#fff)",
                  border: "0.5px solid rgba(0,113,227,0.25)", borderRadius: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text-primary)" }}>
                    AI draft · {uploadPreview.type}
                  </div>
                  <button
                    data-testid="new-tpl-upload-use"
                    onClick={handleUseUploadDraft}
                    style={{
                      background: "var(--green, #1a7a52)", color: "#fff",
                      border: "none", padding: "5px 12px", borderRadius: 7,
                      fontSize: 11, fontWeight: 600, cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Use this draft
                  </button>
                </div>
                {/* Email subject */}
                {uploadPreview.type === "email" && uploadPreview.subject && (
                  <div style={{ fontSize: 11, marginBottom: 6 }}>
                    <strong style={{ color: "var(--text-tertiary)", fontWeight: 600 }}>Subject:</strong> {uploadPreview.subject}
                  </div>
                )}
                {/* Body or sections */}
                {uploadPreview.body && (
                  <pre data-testid="new-tpl-upload-preview-body" style={{
                    fontSize: 11, lineHeight: 1.55, color: "var(--text-secondary)",
                    background: "rgba(0,0,0,0.02)", padding: "8px 10px", borderRadius: 6,
                    maxHeight: 180, overflowY: "auto", whiteSpace: "pre-wrap", margin: 0,
                    fontFamily: "inherit",
                  }}>
                    {uploadPreview.body}
                  </pre>
                )}
                {(uploadPreview.sections || []).length > 0 && (
                  <div data-testid="new-tpl-upload-preview-sections">
                    {uploadPreview.sections.map((s, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 3 }}>{s.title}</div>
                        <pre style={{
                          fontSize: 11, lineHeight: 1.5, color: "var(--text-secondary)",
                          background: "rgba(0,0,0,0.02)", padding: "6px 9px", borderRadius: 6,
                          maxHeight: 110, overflowY: "auto", whiteSpace: "pre-wrap", margin: 0,
                          fontFamily: "inherit",
                        }}>{s.body}</pre>
                      </div>
                    ))}
                  </div>
                )}
                {/* Suggestions */}
                {(uploadPreview.suggestions || []).length > 0 && (
                  <div data-testid="new-tpl-upload-suggestions" style={{
                    marginTop: 8, padding: "8px 10px", background: "rgba(255,159,10,0.06)",
                    border: "0.5px solid rgba(255,159,10,0.25)", borderRadius: 6,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, color: "#8a5500", marginBottom: 4 }}>
                      <Lightbulb style={{ width: 11, height: 11 }} /> AI suggestions
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: "#8a5500", lineHeight: 1.55 }}>
                      {uploadPreview.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {/* Warnings */}
                {(uploadPreview.warnings || []).length > 0 && (
                  <div data-testid="new-tpl-upload-warnings" style={{ marginTop: 6, fontSize: 10.5, color: "var(--text-tertiary)" }}>
                    {uploadPreview.warnings.map((w, i) => <div key={i}>· {w}</div>)}
                  </div>
                )}
                {uploadPreview._source && (
                  <div style={{ marginTop: 6, fontSize: 10, color: "var(--text-tertiary)" }}>
                    Source: {uploadPreview._source.filename} · {uploadPreview._source.word_count} words
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Divider */}
          <div
            style={{
              display: "flex", alignItems: "center", gap: 8,
              fontSize: 10.5, color: "var(--text-tertiary)",
              textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12,
            }}
          >
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            or build manually
            <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
          </div>

          {/* Segments */}
          <FieldLabel hint={`${SEGMENT_SUGGESTIONS.length} suggested`}>
            ✦ Build for an existing segment
          </FieldLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
            {SEGMENT_SUGGESTIONS.map((s) => {
              const on = pickedSegments.includes(s.key);
              return (
                <button
                  key={s.key}
                  data-testid={`new-tpl-seg-${s.key}`}
                  onClick={() => toggleSegment(s.key)}
                  style={{
                    padding: "5px 10px", borderRadius: 20,
                    border: on ? "1px solid rgba(139,92,246,0.4)" : "0.5px solid var(--border)",
                    background: on ? "rgba(139,92,246,0.12)" : "transparent",
                    color: on ? "#5b21b6" : "var(--text-secondary)",
                    fontSize: 11, cursor: "pointer", fontFamily: "inherit",
                    display: "inline-flex", alignItems: "center", gap: 4,
                  }}
                >
                  <Users style={{ width: 11, height: 11 }} /> {s.label} ({s.count})
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--text-tertiary)", marginBottom: 14 }}>
            Selecting a segment pre-fills AI context — the template is written specifically for those leads' specialties, pain points, and product fit.
          </div>

          {/* Name + tags */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <FieldLabel>Template name</FieldLabel>
              <input
                data-testid="new-tpl-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. CCM intro — Cardiology"
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel>Tags</FieldLabel>
              <input
                data-testid="new-tpl-tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="CCM, Cardiology…"
                style={inputStyle}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex", justifyContent: "flex-end", gap: 8,
            padding: "12px 18px", borderTop: "0.5px solid var(--border)",
            background: "var(--page-bg, #fafafa)",
          }}
        >
          <button
            data-testid="new-tpl-cancel"
            onClick={onClose}
            style={btnGhost}
          >
            Cancel
          </button>
          <button
            data-testid="new-tpl-generate-footer"
            onClick={handleGenerate}
            disabled={generating}
            style={{ ...btnPurple, opacity: generating ? 0.7 : 1 }}
          >
            <Sparkles style={{ width: 12, height: 12 }} /> Generate with AI
          </button>
          <button
            data-testid="new-tpl-blank"
            onClick={handleBlank}
            style={btnPrimary}
          >
            Create blank
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div style={{ fontSize: 10.5, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5, display: "flex", justifyContent: "space-between" }}>
      <span>{children}</span>
      {hint && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 11px",
  border: "0.5px solid var(--border)", borderRadius: 8,
  fontSize: 12.5, background: "var(--surface,#fff)",
  outline: "none", boxSizing: "border-box", fontFamily: "inherit",
};

const btnPrimary = {
  background: "#0071e3", color: "#fff", border: "none",
  padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
  cursor: "pointer", fontFamily: "inherit",
};
const btnPurple = {
  background: "#7c3aed", color: "#fff", border: "none",
  padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
  cursor: "pointer", fontFamily: "inherit",
  display: "inline-flex", alignItems: "center", gap: 5,
};
const btnGhost = {
  background: "transparent", color: "var(--text-primary)",
  border: "0.5px solid var(--border)", padding: "7px 14px",
  borderRadius: 8, fontSize: 12, fontWeight: 500,
  cursor: "pointer", fontFamily: "inherit",
};
